// @ts-check
/// <reference path="../../types/index.js" />

import React, { useState, useEffect, useContext } from 'react';
import useAuditLogs from '../../hooks/useAuditLogs';
import SelectTabContextProvider from '../SelectTabContext/SelectTabContext';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@mui/material/CircularProgress';

import {
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

const useModalStyles = makeStyles((theme) => ({
  closeButton: {
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    color: '#3B4149',
    '&:hover': {
      color: '#3B4149',
    },
  },
  tableBox: {
    borderRadius: '5px 0px 0px 0px',
    border: '1px 0px 0px 0px',
    border: '1px solid #D9DDE3',
    boxShadow: '0px 5px 15px 0px #0000001A',
    overflow: 'hidden',
    '& .MuiDialog-paper': {
      width: '80%',
      maxWidth: 'none',
      '@media (max-width: 699px)': {
        width: '100%',
      },
      '@media (min-width: 700px) and (max-width: 1200px)': {
        width: '80%',
      },
    },
  },
  boxContent: {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 64px)',
  },
  searchInput: {
    marginBottom: 16,
  },
  title: {
    border: '1px solid #CFD4DB',
  }
}));

const useDataTableStyles = makeStyles((theme) => ({
  header: {
    "&&": {
      backgroundColor: '#fafafa',
      fontSize: '16px',
      fontWeight: 600,
    }
  },
  tableContainer: {
    height: 500, 
    overflow: 'auto', 
    border: '1px solid #eaeaea' 
  }
}));

export default function AuditLogModal({ open, handleClose }) {
  const modalClasses = useModalStyles();
  const dataTableClasses = useDataTableStyles();

  const { fetchAuditLogs, loading } = useAuditLogs();

  const { auditLogs } = useContext(SelectTabContextProvider);

  const [activeLogs, setActiveLogs] = useState({
    logs: [],
    label: '',
    active: false,
    totalRecords: 0,
  });

  const [prevLogs, setPrevLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (!auditLogs.length) {
      return;
    }
    const currentActiveLog = auditLogs.find((log) => log.active);
    if (typeof currentActiveLog === 'undefined' || !currentActiveLog) {
      return;
    }
    setActiveLogs(currentActiveLog);
    setPrevLogs(currentActiveLog.logs);
  }, [auditLogs]);

  const filteredLogs = activeLogs.logs.filter((log) =>
    Object.values(log).some((value) =>
      String(value).toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setPrevLogs(activeLogs.logs);
    fetchAuditLogs(activeLogs.label, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog
      open={open}
      className={modalClasses.tableBox}
      onClose={handleClose}
    >
      <DialogTitle className={modalClasses.title}>
        Audit Log
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent className={modalClasses.boxContent}>
        <Grid container spacing={2} sx={{ marginBottom: '10px' }} alignItems="center" justifyContent="space-between">
          <Grid item xs={true}>
            <Typography variant="h6" component="h2">
              Patient Account Information
            </Typography>
          </Grid>
          <Grid item>
            <TextField
              label="Search..."
              variant="outlined"
              className={modalClasses.searchInput}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton edge="start">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)', 
              zIndex: 1 
            }}>
              <CircularProgress style={{ color: 'rgb(44, 21, 112)' }} size={24} />
            </Box>
          )}
          <TableContainer className={dataTableClasses.tableContainer}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell className={dataTableClasses.header}>Date</TableCell>
                  <TableCell className={dataTableClasses.header}>Field</TableCell>
                  <TableCell className={dataTableClasses.header}>Description</TableCell>
                  <TableCell className={dataTableClasses.header}>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(loading ? prevLogs : filteredLogs).map((row, index) => (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id || index}
                    sx={{ bgcolor: loading ? 'rgba(0, 0, 0, 0.1)' : 'inherit' }}
                  >
                    <TableCell>{row.updated_dt}</TableCell>
                    <TableCell>{row.field.split('.')[1]}</TableCell>
                    <TableCell>{row.value}</TableCell>
                    <TableCell>{row.updated_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          rowsPerPageOptions={[200]}
          component="div"
          count={activeLogs.totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DialogContent>
    </Dialog>
  );
}
